// Standalone page (sem sidebar/topbar do TDAX). Etapa 1 cria o workspace —
// dali o cliente pode pular o resto (tela de sucesso com checklist).
import { useState } from "react";

import { Icon } from "@/components/common/Icon";
import { OnbFootNav } from "@/components/onboarding/OnbFootNav";
import { OnbShell } from "@/components/onboarding/OnbShell";
import { Field } from "@/components/onboarding/OnbFields";
import { Toggle } from "@/components/common";
import type {
  WorkspaceInvitationAcceptPayload,
  WorkspaceInvitationPrefill,
} from "@/services/invitationService";

import { useStepWizard } from "./useStepWizard";
import { WsStepEquipe } from "./WsStepEquipe";
import { WsStepWorkspace } from "./WsStepWorkspace";
import { WsSuccess } from "./WsSuccess";
import {
  WS_INITIAL_FORM,
  WS_STEPS,
  type WsForm,
  type WsSetField,
} from "./types";

interface Props {
  token?: string;
  onExit?: () => void;
  // Quando vem do fluxo publico (link de convite), submit POST /accept.
  onSubmit?: (payload: WorkspaceInvitationAcceptPayload) => Promise<void>;
  suggestedName?: string | null;
  emailMasked?: string;
  prefill?: WorkspaceInvitationPrefill | null;
  submitError?: string | null;
  isSubmitting?: boolean;
}

function buildAcceptPayload(form: WsForm): WorkspaceInvitationAcceptPayload {
  return {
    password: form.ownerSenha,
    phone: form.ownerTelefone,
    workspace_name: form.nomeWorkspace,
    accepted_tos: form.aceiteTermos,
    marketing_consent: false,
    doc_type: form.docType,
    cnpj: form.docType === "cnpj" ? form.cnpj : null,
    cpf: form.docType === "cpf" ? form.cpf : null,
    razao_social: form.razao || null,
    // CONS-006/CONS-007: WorkspaceInvitationAcceptRequest usa extra="forbid" e NAO
    // tem campo nome_fantasia/nickname p/ workspace. O `fantasia` digitado fica só no
    // form local (exibido no resumo); não é enviado no accept (causaria 422).
    address: form.endereco || null,
    address_number: form.numero || null,
    address_complement: form.complemento || null,
    address_neighborhood: form.bairro || null,
    address_zip: form.cep || null,
    uf: form.uf || null,
    cidade: form.cidade || null,
    company_phone: form.telefone || null,
    company_email: form.emailEmpresa || null,
    legal_rep_name: form.respNome || null,
    legal_rep_cpf: form.respCpf || null,
    legal_rep_role: form.respCargo || null,
    owner_name: form.ownerNome || null,
  };
}

export function WorkspaceOnboarding({
  onExit, onSubmit, suggestedName, prefill, submitError, isSubmitting,
}: Props) {
  const {
    stepId,
    completed,
    skipped,
    done,
    stepIdx,
    completedCount,
    isLast,
    markCompletedAndAdvance,
    markSkippedAndAdvance,
    goBack,
    goStep,
  } = useStepWizard(WS_STEPS);
  const [form, setForm] = useState<WsForm>(() => ({
    ...WS_INITIAL_FORM,
    nomeWorkspace: suggestedName || "",
    docType: prefill?.doc_type ?? WS_INITIAL_FORM.docType,
    cnpj: prefill?.cnpj ?? "",
    cpf: prefill?.cpf ?? "",
    razao: prefill?.razao_social ?? "",
    fantasia: prefill?.nome_fantasia ?? "",
    nomePf: prefill?.nome_pf ?? "",
    endereco: prefill?.address ?? "",
    cidade: prefill?.cidade ?? "",
    uf: prefill?.uf ?? "",
    telefone: prefill?.company_phone ?? "",
    emailEmpresa: prefill?.company_email ?? "",
    respNome: prefill?.legal_rep_name ?? "",
    respCpf: prefill?.legal_rep_cpf ?? "",
    respCargo: prefill?.legal_rep_role ?? "",
  }));

  const setField = <K extends keyof WsForm>(k: K, v: WsForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const goNext = async () => {
    // Step 1 (workspace) e o ponto de criacao. Se temos onSubmit, dispara POST.
    // Em caso de sucesso, avanca pros steps opcionais (config + equipe).
    if (WS_STEPS[stepIdx].id === "workspace" && onSubmit) {
      try {
        await onSubmit(buildAcceptPayload(form));
      } catch {
        return; // erro ja propagado via submitError prop; nao avanca.
      }
    }
    markCompletedAndAdvance();
  };
  const goSkip = () => markSkippedAndAdvance();
  const exitToDashboard = () => onExit?.();

  if (done) {
    return (
      <OnbShell
        stepId={stepId}
        STEPS={WS_STEPS}
        completed={completed}
        skipped={skipped}
        onGoStep={goStep}
        completedCount={completedCount}
        onExit={exitToDashboard}
      >
        <WsSuccess
          form={form}
          completed={completed}
          onResume={goStep}
          onExit={exitToDashboard}
        />
      </OnbShell>
    );
  }

  const curStep = WS_STEPS[stepIdx];

  // Validação client-side do step 1 (criação, obrigatório). Os demais são
  // opcionais e não bloqueiam.
  const workspaceStepValid =
    !!form.nomeWorkspace.trim() &&
    !!form.ownerNome.trim() &&
    !!form.ownerEmail.trim() &&
    !!form.ownerTelefone.trim() &&
    form.ownerSenha.length >= 8 &&
    form.ownerSenha === form.ownerSenha2 &&
    form.aceiteTermos &&
    form.aceiteLgpd;
  const canContinue = curStep.id === "workspace" ? workspaceStepValid : true;

  return (
    <OnbShell
      stepId={stepId}
      STEPS={WS_STEPS}
      completed={completed}
      skipped={skipped}
      onGoStep={goStep}
      completedCount={completedCount}
      onExit={exitToDashboard}
    >
      {stepId === "workspace" && (
        <WsStepWorkspace form={form} setField={setField} />
      )}
      {stepId === "config" && (
        <WsStepConfig form={form} setField={setField} />
      )}
      {stepId === "equipe" && <WsStepEquipe form={form} setForm={setForm} />}

      <OnbFootNav
        canGoBack={stepIdx > 0}
        onBack={goBack}
        canSkip={!curStep.required}
        onSkip={goSkip}
        onNext={goNext}
        isLast={isLast}
        isSubmitting={isSubmitting}
        canContinue={canContinue}
      />
      {submitError && (
        <div className="onb-submit-error" role="alert">
          <Icon name="alert-triangle" size={14} />
          <span>{submitError}</span>
        </div>
      )}
    </OnbShell>
  );
}

interface WsStepConfigProps {
  form: WsForm;
  setField: WsSetField;
}

const WS_COLORS = ["#F4CC04", "#38BDF8", "#2DD4BF", "#A78BFA", "#FB923C", "#F87171"];

function WsStepConfig({ form, setField }: WsStepConfigProps) {
  return (
    <>
      <div className="onb-section-head">
        <div className="onb-step-eyebrow">ETAPA 2 · OPCIONAL · ILUSTRATIVO</div>
        <h1 className="onb-step-title">Personalize seu workspace</h1>
        <p className="onb-step-sub">
          Pré-visualização de cor da marca, fuso horário e canais de notificação.
          Essas preferências ainda não são salvas no onboarding — configure-as
          nas configurações do workspace após entrar.
        </p>
      </div>

      <div className="onb-section">
        <div className="onb-section-title">Identidade visual</div>
        <Field label="Cor da marca">
          <div className="onb-color-row">
            {WS_COLORS.map((c) => (
              <button
                key={c}
                className={`onb-color-sw ${form.corMarca === c ? "is-on" : ""}`}
                style={{ background: c }}
                onClick={() => setField("corMarca", c)}
                aria-label={c}
              />
            ))}
          </div>
        </Field>
        <Field
          label="Logo do workspace"
          hint="configure nas configurações do workspace após entrar"
        >
          <div className="onb-file-drop compact is-disabled" aria-disabled="true">
            <Icon name="building" size={16} />
            <div className="onb-file-drop-text">
              <div className="onb-file-drop-title">
                Upload de logo disponível nas configurações
              </div>
              <div className="onb-file-drop-sub">PNG ou SVG · até 1 MB</div>
            </div>
          </div>
        </Field>
      </div>

      <div className="onb-section">
        <div className="onb-section-title">Fuso horário</div>
        <Field label="Selecione o fuso">
          <select
            className="onb-input"
            value={form.fuso}
            onChange={(e) => setField("fuso", e.target.value)}
          >
            <option value="America/Sao_Paulo">São Paulo (BRT, UTC-3)</option>
            <option value="America/Manaus">Manaus (AMT, UTC-4)</option>
            <option value="America/Recife">Recife (BRT, UTC-3)</option>
            <option value="America/Belem">Belém (BRT, UTC-3)</option>
            <option value="America/Cuiaba">Cuiabá (AMT, UTC-4)</option>
          </select>
        </Field>
      </div>

      <div className="onb-section">
        <div className="onb-section-title">Notificações</div>
        <Toggle
          checked={form.notifEmail}
          onChange={(v) => setField("notifEmail", v)}
          label="Notificações por e-mail"
          sub="Resumo diário · alertas críticos · convites"
        />
        <Toggle
          checked={form.notifWhatsapp}
          onChange={(v) => setField("notifWhatsapp", v)}
          label="Notificações por WhatsApp"
          sub="Apenas alertas críticos · 1 mensagem por dia no máximo"
        />
      </div>
    </>
  );
}
